/**
 * Spend & Earn — Tier Progress (theme-1)
 *
 * CSS-first architecture. JS sets target state, CSS transitions animate.
 *
 * Shopify section rendering destroys and recreates this element via innerHTML.
 * Static class properties survive because the class definition persists.
 *
 * Two render modes:
 *   render(total, false) — snap instantly (adds .se--no-anim to kill transitions)
 *   animateTo(total)     — sequences unlock: fill→celebrate→swap, or just render for simple changes
 *
 * Animation cancellation uses AbortController — any new cart event or
 * disconnection aborts in-flight animations and snaps to the correct state.
 */

if (!customElements.get('spend-and-earn')) {

  // CSS durations (must match spend-and-earn.css)
  const T = {
    FILL:       1200,  // .se__fill width transition (normal progress)
    FILL_FAST:   500,  // fill during unlock sequence (snappier)
    GRID:        700,  // .se__tier + .se__bar grid-template-rows
    CELEBRATE:   500,  // marker pop animation
    PAUSE:       600,  // pause after celebration
    CHECK_HOLD:  400,  // hold check visible before collapse
    SETTLE:      200,  // breathing room between phases
  };

  class SpendAndEarn extends HTMLElement {

    // ── Static (survives innerHTML replacement) ──
    static prev = { total: 0, expanded: false, unlockedExpanded: false };
    static isCartUpdating = false;
    static cartUpdateTimestamp = 0;
    static APPROACH = 0.85;
    static _debounceTimer = null;
    static _instanceCounter = 0;
    static _animating = false;
    static _activeAnimations = [];

    constructor() {
      super();
      this.rewards = [];
      this.tiers = [];
      this.el = {};
      this.locale = {};
    }

    // ════════════════════════════════════════════
    // LIFECYCLE
    // ════════════════════════════════════════════

    connectedCallback() {
      this.rewards = JSON.parse(this.dataset.rewards || '[]')
        .sort((a, b) => a.threshold - b.threshold);
      if (!this.rewards.length) return;

      this._instanceId = ++SpendAndEarn._instanceCounter;

      this.locale = JSON.parse(this.dataset.locale || '{}');
      this.expanded = SpendAndEarn.prev.expanded;
      this.unlockedExpanded = SpendAndEarn.prev.unlockedExpanded;
      this.cache();
      this.reorder();

      const newTotal = parseInt(this.dataset.cartTotal, 10) || 0;
      const oldTotal = SpendAndEarn.prev.total;
      const timeSince = Date.now() - SpendAndEarn.cartUpdateTimestamp;
      const wasRecreated = SpendAndEarn.isCartUpdating || timeSince < 500;

      if (wasRecreated && oldTotal !== newTotal && this._isLatest()) {
        // Section re-render: snap to old state, double-rAF, animate to new
        this.render(oldTotal, false);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this._isLatest()) this.animateTo(newTotal, oldTotal);
          });
        });
      } else {
        this.render(newTotal, false);
      }

      SpendAndEarn.prev.total = newTotal;
      SpendAndEarn.isCartUpdating = false;

      // Events
      this._onStart = () => {
        SpendAndEarn.isCartUpdating = true;
        SpendAndEarn.cartUpdateTimestamp = Date.now();
      };
      document.addEventListener('line-item:change:start', this._onStart);
      document.addEventListener('cart:item-added', this._onStart);
      document.addEventListener('line-item:change:end', this._onEnd = e => this.onCart(e));
      document.addEventListener('line-item:change:error', this._onError = () => {
        SpendAndEarn.isCartUpdating = false;
      });
      this.el.expand?.addEventListener('click', this._onExpand = () => this.toggle());
      this.el.unlockedSummary?.addEventListener('click', this._onUnlockedToggle = () => this.toggleUnlocked());

      // Theme editor: re-render when section reloads (JS doesn't re-execute)
      document.addEventListener('shopify:section:load', this._onSectionLoad = (e) => {
        if (this.contains(e.target) || e.target.contains(this)) {
          const total = parseInt(this.dataset.cartTotal, 10) || 0;
          this.render(total, false);
        }
      });

      // Track hover shimmer
      this.tiers.forEach(t => {
        if (!t.fill || !t.track) return;
        let shimmerActive = false;
        t.track.addEventListener('mouseenter', () => {
          if (shimmerActive) return;
          shimmerActive = true;
          this.triggerFillAnimation(t.fill);
          const id = setTimeout(() => { shimmerActive = false; }, 3000);
          this._abort?.signal?.addEventListener('abort', () => { clearTimeout(id); shimmerActive = false; });
        });
      });
    }

    disconnectedCallback() {
      if (this._onStart) {
        document.removeEventListener('line-item:change:start', this._onStart);
        document.removeEventListener('cart:item-added', this._onStart);
      }
      if (this._onEnd) document.removeEventListener('line-item:change:end', this._onEnd);
      if (this._onError) document.removeEventListener('line-item:change:error', this._onError);
      if (this._onSectionLoad) document.removeEventListener('shopify:section:load', this._onSectionLoad);
      clearTimeout(SpendAndEarn._debounceTimer);
      this.cancelAnimations();
    }

    _isLatest() {
      return this._instanceId === SpendAndEarn._instanceCounter;
    }

    // ════════════════════════════════════════════
    // ABORT CONTROLLER
    // ════════════════════════════════════════════

    cancelAnimations() {
      this._abort?.abort();
      this._abort = null;
      SpendAndEarn._animating = false;
      // Cancel all tracked Web Animations (confetti/particles) and remove their elements
      SpendAndEarn._activeAnimations.forEach(({ anim, el }) => {
        try { anim.cancel(); } catch {}
        el?.remove();
      });
      SpendAndEarn._activeAnimations = [];
      // Remove any in-flight CSS animation classes
      this.querySelectorAll('.se__tier--exiting').forEach(el => {
        el.classList.remove('se__tier--exiting');
      });
      // Snap any running CSS transitions to their current computed values
      this.classList.add('se--no-anim');
      void this.offsetWidth;
      this.classList.remove('se--no-anim');
    }

    _signal() {
      this.cancelAnimations();
      this._abort = new AbortController();
      return this._abort.signal;
    }

    _delay(ms, signal) {
      return new Promise((resolve, reject) => {
        const id = setTimeout(resolve, ms);
        signal?.addEventListener('abort', () => {
          clearTimeout(id);
          reject(new DOMException('Aborted', 'AbortError'));
        });
      });
    }

    // ════════════════════════════════════════════
    // DOM CACHE
    // ════════════════════════════════════════════

    cache() {
      this.el = {
        msg: this.querySelector('[data-msg]'),
        confetti: this.querySelector('[data-confetti]'),
        tiers: this.querySelector('[data-tiers]'),
        expand: this.querySelector('[data-expand]'),
        expandText: this.querySelector('[data-expand-text]'),
        unlockedSummary: this.querySelector('[data-unlocked-summary]'),
        unlockedBadges: this.querySelector('[data-unlocked-badges]'),
        unlockedText: this.querySelector('[data-unlocked-text]'),
      };

      this.tiers = [];
      this.rewards.forEach(r => {
        const row = this.querySelector(
          `[data-tier][data-type="${r.type}"][data-threshold="${r.threshold}"]`
        );
        if (row) {
          this.tiers.push({
            row,
            fill: row.querySelector('[data-fill]'),
            marker: row.querySelector('[data-marker]'),
            track: row.querySelector('[role="progressbar"]'),
          });
        }
      });
    }

    reorder() {
      if (!this.el.tiers) return;
      this.tiers.forEach(t => t.row && this.el.tiers.appendChild(t.row));
    }

    // ════════════════════════════════════════════
    // SET TIER STATE — single tier, no side effects
    // ════════════════════════════════════════════

    setTierState(t, i, state, fillPct) {
      t.row.classList.remove(
        'se__tier--locked', 'se__tier--active', 'se__tier--unlocked', 'se__tier--hidden'
      );
      t.row.classList.add(`se__tier--${state}`);
      if (state === 'locked' && !this.expanded) t.row.classList.add('se__tier--hidden');
      if (t.fill) t.fill.style.width = `${fillPct}%`;
      if (t.track) t.track.setAttribute('aria-valuenow', Math.round(fillPct));
    }

    snapFill(fill, pct) {
      if (!fill) return;
      fill.style.transition = 'none';
      fill.style.width = `${pct}%`;
      void fill.offsetWidth;
      fill.style.transition = '';
    }

    // ════════════════════════════════════════════
    // RENDER — instant snap to a state
    // ════════════════════════════════════════════

    render(total, animate) {
      const ri = this.idx(total);

      if (!animate) this.classList.add('se--no-anim');

      this.tiers.forEach((t, i) => {
        t.row.classList.remove(
          'se__tier--locked', 'se__tier--active', 'se__tier--unlocked',
          'se__tier--hidden'
        );
        if (t.marker) {
          t.marker.classList.remove('se__marker--approaching', 'se__marker--done', 'se__marker--celebrating');
        }

        let pctVal = 0;
        if (i < ri) {
          t.row.classList.add('se__tier--unlocked');
          if (!this.unlockedExpanded) t.row.classList.add('se__tier--hidden');
          if (t.fill) t.fill.style.width = '100%';
          pctVal = 100;
        } else if (i === ri) {
          t.row.classList.add('se__tier--active');
          pctVal = this.pct(total);
          if (t.fill) t.fill.style.width = `${pctVal}%`;
          if (pctVal / 100 >= SpendAndEarn.APPROACH && t.marker) {
            t.marker.classList.add('se__marker--approaching');
          }
        } else {
          t.row.classList.add('se__tier--locked');
          if (!this.expanded) t.row.classList.add('se__tier--hidden');
          if (t.fill) t.fill.style.width = '0%';
        }

        if (t.track) t.track.setAttribute('aria-valuenow', Math.round(pctVal));
      });

      if (this.el.msg) this.el.msg.innerHTML = this.msgText(total);
      this.renderUnlockedSummary(ri);
      this.renderExpand(ri);

      if (!animate) {
        void this.offsetWidth;
        this.classList.remove('se--no-anim');
      }

    }

    // ════════════════════════════════════════════
    // ANIMATE — sequenced transitions
    // ════════════════════════════════════════════

    /**
     * Animate from current visual state to target total.
     * For unlocks: walks through each tier sequentially (handles multi-tier skips).
     * For simple progress: just renders directly (CSS transitions handle it).
     * Uses AbortController so any new cart event cancels in-flight animation.
     *
     * KEY DESIGN: never calls render() mid-sequence. Instead, manipulates individual
     * tier classes directly. render() is called once at the end to sync final state.
     */
    async animateTo(newTotal, oldTotal) {
      if (oldTotal === undefined) {
        oldTotal = parseInt(this.dataset.cartTotal, 10) || 0;
      }

      const oldIdx = this.idx(oldTotal);
      const newIdx = this.idx(newTotal);

      this.dataset.cartTotal = newTotal;
      SpendAndEarn.prev.total = newTotal;

      // Same tier — CSS transitions handle the fill change
      if (newIdx === oldIdx) {
        this.render(newTotal, true);
        return;
      }

      // Stepped back — sequenced deflation
      if (newIdx < oldIdx) {
        return this.animateBack(newTotal, oldTotal);
      }

      // ── Unlock sequence (walks each tier) ──
      SpendAndEarn._animating = true;
      const signal = this._signal();

      try {
        for (let i = oldIdx; i < newIdx; i++) {
          const tier = this.tiers[i];
          const next = this.tiers[i + 1];
          const isLast = (i === newIdx - 1);
          const isFirst = (i === oldIdx);

          // Phase 1: Fill current tier to 100% (fast)
          // Skip if previous iteration already filled this tier in Phase 7
          if (isFirst && tier?.fill) {
            tier.fill.classList.add('se__fill--fast');
            this.spawnGoldParticles(tier, 8);
            this.triggerFillAnimation(tier.fill);
            tier.fill.style.width = '100%';
            if (tier.track) tier.track.setAttribute('aria-valuenow', '100');
            await this._delay(T.FILL_FAST, signal);
            tier.fill.classList.remove('se__fill--fast');
          }

          // Phase 2: Celebrate — marker pops, confetti, vibrate
          if (tier?.marker) {
            this.setMarker(tier, 'done');
            tier.marker.classList.add('se__marker--celebrating');
            this.spawnGoldParticles(tier, 12);
            this.confetti(tier.marker, i === this.rewards.length - 1);
            try { navigator.vibrate?.([50, 30, 50]); } catch {}
          }
          await this._delay(T.CELEBRATE, signal);

          // Phase 3: Hold — celebration pop ends, check stays visible
          tier?.marker?.classList.remove('se__marker--celebrating');
          await this._delay(T.PAUSE, signal);

          // Phase 4: Hold completed state — let user admire the gold check + full bar
          await this._delay(T.CHECK_HOLD, signal);

          // Phase 5: Snap the tier swap — celebration was the reward, this is just infrastructure
          this.renderUnlockedSummary(i + 1);
          this.classList.add('se--no-anim');
          this.setTierState(tier, i, 'unlocked', 100);
          tier.row.classList.add('se__tier--hidden');
          if (next) {
            this.snapFill(next.fill, 0);
            this.setTierState(next, i + 1, 'active', 0);
          }
          this.renderExpand(i + 1);
          if (this.el.msg) {
            const interimTotal = isLast ? newTotal : this.rewards[i].threshold;
            this.el.msg.innerHTML = this.msgText(interimTotal);
          }
          void this.offsetWidth;
          this.classList.remove('se--no-anim');
          await this._delay(T.SETTLE, signal);

          if (next?.fill) {
            const targetPct = isLast ? this.pct(newTotal) : 100;
            next.fill.classList.add('se__fill--fast');
            this.triggerFillAnimation(next.fill);
            this.spawnGoldParticles(next, 6);
            next.fill.style.width = `${targetPct}%`;
            if (targetPct / 100 >= SpendAndEarn.APPROACH && next.marker) {
              next.marker.classList.add('se__marker--approaching');
            }
            await this._delay(T.FILL_FAST, signal);
            next.fill.classList.remove('se__fill--fast');
          } else {
            await this._delay(T.FILL_FAST, signal);
          }
        }

        // Auto-collapse unlocked tiers after animation
        this.unlockedExpanded = false;
        SpendAndEarn.prev.unlockedExpanded = false;

        // Sync final state (snap — imperative animations already handled visuals)
        SpendAndEarn._animating = false;
        this.render(newTotal, false);
      } catch (e) {
        if (e.name !== 'AbortError') throw e;
        SpendAndEarn._animating = false;
        this.render(newTotal, false);
      }
    }

    // ════════════════════════════════════════════
    // ANIMATE BACK — sequenced deflation for stepping down tiers
    // ════════════════════════════════════════════

    /**
     * Animate backwards when items are removed and we drop to a lower tier.
     * Three phases: drain current fill → snap state → fill new active tier.
     * No competing grid transitions — the snap is instant.
     */
    async animateBack(newTotal, oldTotal) {
      SpendAndEarn._animating = true;
      const signal = this._signal();
      const oldIdx = this.idx(oldTotal);
      const newIdx = this.idx(newTotal);

      try {
        // Phase 1: Drain current active tier's fill to 0
        const active = this.tiers[oldIdx];
        if (active?.fill) {
          active.fill.classList.add('se__fill--fast');
          active.fill.style.width = '0%';
          if (active.track) active.track.setAttribute('aria-valuenow', '0');
          await this._delay(T.FILL_FAST, signal);
          active.fill.classList.remove('se__fill--fast');
        }

        // Phase 2: Snap to new state (all transitions disabled)
        this.classList.add('se--no-anim');

        this.tiers.forEach((t, i) => {
          if (t.marker) {
            t.marker.classList.remove('se__marker--approaching', 'se__marker--done', 'se__marker--celebrating');
          }
          if (i < newIdx) {
            this.setTierState(t, i, 'unlocked', 100);
            if (!this.unlockedExpanded) t.row.classList.add('se__tier--hidden');
          } else if (i === newIdx) {
            this.setTierState(t, i, 'active', 0);
          } else {
            this.setTierState(t, i, 'locked', 0);
            if (!this.expanded) t.row.classList.add('se__tier--hidden');
          }
        });

        if (this.el.msg) this.el.msg.innerHTML = this.msgText(newTotal);
        this.renderUnlockedSummary(newIdx);
        this.renderExpand(newIdx);

        void this.offsetWidth;
        this.classList.remove('se--no-anim');

        // Phase 3: Animate fill to target
        await this._delay(50, signal);

        const newActive = this.tiers[newIdx];
        const targetPct = this.pct(newTotal);
        if (newActive?.fill) {
          this.triggerFillAnimation(newActive.fill);
          newActive.fill.style.width = `${targetPct}%`;
          if (newActive.track) newActive.track.setAttribute('aria-valuenow', Math.round(targetPct));
          if (targetPct / 100 >= SpendAndEarn.APPROACH && newActive.marker) {
            newActive.marker.classList.add('se__marker--approaching');
          }
        }

        SpendAndEarn._animating = false;
      } catch (e) {
        if (e.name !== 'AbortError') throw e;
        SpendAndEarn._animating = false;
        this.render(newTotal, false);
      }
    }

    // ════════════════════════════════════════════
    // CART EVENTS
    // ════════════════════════════════════════════

    onCart(e) {
      if (!this._isLatest()) return;

      const cart = e.detail?.cart || {};
      const newTotal = this.eligible(cart);

      SpendAndEarn.isCartUpdating = false;

      const prevTotal = parseInt(this.dataset.cartTotal, 10) || 0;
      if (newTotal === prevTotal) return;

      // Tier-crossing changes fire immediately; simple progress debounces
      const oldIdx = this.idx(prevTotal);
      const newIdx = this.idx(newTotal);

      clearTimeout(SpendAndEarn._debounceTimer);

      if (newIdx !== oldIdx) {
        this.cancelAnimations();
        this.animateTo(newTotal, prevTotal);
      } else {
        SpendAndEarn._debounceTimer = setTimeout(() => {
          this.cancelAnimations();
          this.animateTo(newTotal, prevTotal);
        }, 300);
      }
    }

    // ════════════════════════════════════════════
    // EXPAND / COLLAPSE
    // ════════════════════════════════════════════

    toggle() {
      this.expanded = !this.expanded;
      SpendAndEarn.prev.expanded = this.expanded;
      const total = parseInt(this.dataset.cartTotal, 10) || 0;
      this.render(total, true);
    }

    toggleUnlocked() {
      this.unlockedExpanded = !this.unlockedExpanded;
      SpendAndEarn.prev.unlockedExpanded = this.unlockedExpanded;
      const total = parseInt(this.dataset.cartTotal, 10) || 0;
      this.render(total, true);
    }

    renderUnlockedSummary(ri) {
      const summary = this.el.unlockedSummary;
      if (!summary) return;

      summary.classList.remove('se__unlocked-summary--hidden');
      summary.classList.toggle('se__unlocked-summary--open', this.unlockedExpanded && ri > 0);
      summary.classList.toggle('se__unlocked-summary--empty', ri <= 0);
      summary.setAttribute('aria-expanded', String(this.unlockedExpanded));

      // Build badges
      if (this.el.unlockedBadges) {
        const badges = [];
        for (let i = 0; i < ri; i++) {
          const r = this.rewards[i];
          const icon = r.type === 'shipping'
            ? '<svg class="se__svg se__svg--truck" viewBox="0 0 24 24" fill="none"><path d="M16 3H1V16H16V3Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 8H20L23 11V16H16V8Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="5.5" cy="18.5" r="2.5" stroke="currentColor" stroke-width="2"/><circle cx="18.5" cy="18.5" r="2.5" stroke="currentColor" stroke-width="2"/></svg>'
            : '<svg class="se__svg" viewBox="0 0 24 24" fill="none"><path d="M8 12L11 15L16 9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          badges.push(`<span class="se__unlocked-badge">${icon}</span>`);
        }
        this.el.unlockedBadges.innerHTML = badges.join('');
      }

      // Text
      if (this.el.unlockedText) {
        const L = this.locale;
        if (ri <= 0) {
          const tpl = L.rewards_unlocked || '{{ count }} rewards unlocked';
          this.el.unlockedText.textContent = this._tpl(tpl, { count: 0 });
        } else if (this.unlockedExpanded) {
          this.el.unlockedText.textContent = L.hide_unlocked || 'Hide unlocked';
        } else {
          const tpl = ri === 1
            ? (L.reward_unlocked || '{{ count }} reward unlocked')
            : (L.rewards_unlocked || '{{ count }} rewards unlocked');
          this.el.unlockedText.textContent = this._tpl(tpl, { count: ri });
        }
      }
    }

    renderExpand(ri) {
      const btn = this.el.expand;
      if (!btn) return;
      const locked = this.rewards.length - Math.min(ri + 1, this.rewards.length);
      if (locked <= 0) {
        btn.classList.add('se__expand--hidden');
        return;
      }
      btn.classList.remove('se__expand--hidden');
      if (this.el.expandText) {
        const L = this.locale;
        if (this.expanded) {
          this.el.expandText.textContent = L.hide || 'Hide rewards';
        } else {
          const tpl = locked === 1 ? (L.more || '{{ count }} more reward') : (L.more_plural || '{{ count }} more rewards');
          this.el.expandText.textContent = this._tpl(tpl, { count: locked });
        }
      }
      btn.setAttribute('aria-expanded', String(this.expanded));
      btn.classList.toggle('se__expand--open', this.expanded);
    }

    // ════════════════════════════════════════════
    // CALCULATIONS
    // ════════════════════════════════════════════

    idx(total) {
      let i = 0;
      for (const r of this.rewards) {
        if (total >= r.threshold) i++; else break;
      }
      return i;
    }

    pct(total) {
      const i = this.idx(total);
      if (i >= this.rewards.length) return 100;
      const lo = i > 0 ? this.rewards[i - 1].threshold : 0;
      const hi = this.rewards[i].threshold;
      return hi <= lo ? 100 : Math.min(100, Math.max(0, ((total - lo) / (hi - lo)) * 100));
    }

    // ════════════════════════════════════════════
    // VISUAL HELPERS
    // ════════════════════════════════════════════

    setMarker(t, state) {
      if (!t.marker) return;
      t.marker.classList.remove('se__marker--approaching', 'se__marker--done', 'se__marker--celebrating');
      if (state) t.marker.classList.add(`se__marker--${state}`);
    }

    confetti(origin, isFinal = false) {
      const c = this.el.confetti;
      if (!c || !origin || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const or = origin.getBoundingClientRect();
      const cr = c.getBoundingClientRect();
      const cx = or.left - cr.left + or.width / 2;
      const cy = or.top - cr.top + or.height / 2;
      const colors = ['#936347', '#B8860B', '#D4AF37', '#F4D03F', '#f3f2ec', '#E5DED5', '#C9A86C', '#8B5A3C'];
      const count = isFinal ? 60 : 40;
      const maxDist = isFinal ? 250 : 180;
      const baseDuration = isFinal ? 1600 : 1200;
      for (let i = 0; i < count; i++) {
        const p = document.createElement('div');
        p.className = 'confetti-piece';
        const sz = 6 + Math.random() * 8;
        p.style.cssText = `background:${colors[i % colors.length]};left:${cx - sz/2}px;top:${cy - sz/2}px;width:${sz}px;height:${sz}px;border-radius:${Math.random() > .5 ? '50%' : '2px'}`;
        c.appendChild(p);
        const a = Math.random() * Math.PI * 2, d = Math.random() * maxDist;
        const entry = { el: p, anim: null };
        const anim = p.animate([
          { transform: 'translate(0,0) rotate(0deg) scale(1)', opacity: 1 },
          { transform: `translate(${Math.cos(a)*d}px,${Math.sin(a)*d}px) rotate(${360+Math.random()*720}deg) scale(0.2)`, opacity: 0 }
        ], {
          duration: baseDuration + Math.random() * 600,
          delay: Math.random() * 100,
          easing: 'cubic-bezier(0.25,0.46,0.45,0.94)',
          fill: 'forwards'
        });
        entry.anim = anim;
        SpendAndEarn._activeAnimations.push(entry);
        anim.onfinish = () => {
          p.remove();
          const idx = SpendAndEarn._activeAnimations.indexOf(entry);
          if (idx !== -1) SpendAndEarn._activeAnimations.splice(idx, 1);
        };
      }
    }

    triggerFillAnimation(fill) {
      if (!fill) return;
      fill.classList.remove('se__fill--animating');
      void fill.offsetWidth;
      fill.classList.add('se__fill--animating');
      const id = setTimeout(() => fill?.classList.remove('se__fill--animating'), 3000);
      this._abort?.signal?.addEventListener('abort', () => clearTimeout(id));
    }

    spawnGoldParticles(tier, count = 8) {
      const container = tier?.fill;
      if (!container || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      for (let i = 0; i < count; i++) {
        const p = document.createElement('div');
        p.className = 'gold-particle';
        p.style.left = `${Math.random() * 100}%`;
        p.style.top = `${Math.random() * 100}%`;
        container.appendChild(p);
        const entry = { el: p, anim: null };
        const anim = p.animate([
          { opacity: 0, transform: 'scale(0) translateY(0)' },
          { opacity: 1, transform: 'scale(1.5) translateY(-5px)', offset: 0.3 },
          { opacity: 0.8, transform: 'scale(1) translateY(-10px)', offset: 0.7 },
          { opacity: 0, transform: 'scale(0.5) translateY(-20px)' }
        ], {
          duration: 800 + Math.random() * 400,
          delay: Math.random() * 200,
          easing: 'ease-out'
        });
        entry.anim = anim;
        SpendAndEarn._activeAnimations.push(entry);
        anim.onfinish = () => {
          p.remove();
          const idx = SpendAndEarn._activeAnimations.indexOf(entry);
          if (idx !== -1) SpendAndEarn._activeAnimations.splice(idx, 1);
        };
      }
    }

    // ════════════════════════════════════════════
    // MESSAGE & UTILITY
    // ════════════════════════════════════════════

    msgText(total) {
      const fmt = window.theme?.settings?.money_with_currency_format || '${{amount}}';
      const L = this.locale;
      const ri = this.idx(total);

      if (ri >= this.rewards.length) {
        if (this.rewards.length === 1) {
          const r = this.rewards[0];
          const name = r.type === 'shipping' ? (L.free_shipping || 'FREE SHIPPING!') : (r.reward || 'FREE gift!');
          return this._tpl(L.unlocked_one || "You've unlocked {{ reward }}", {
            reward: `<span class="spend-emphasis">${name}</span>`
          });
        }
        return L.unlocked_all || 'You\'ve unlocked all <span class="spend-emphasis">rewards!</span>';
      }

      const r = this.rewards[ri];
      const rem = r.threshold - total;
      const name = r.type === 'shipping' ? (L.free_shipping || 'FREE SHIPPING!') : (r.reward || 'FREE gift!');
      const amount = this.money(rem, fmt);
      const p = this.pct(total) / 100;
      const tpl = p >= 0.85
        ? (L.so_close || 'So close! Just {{ amount }} away from {{ reward }}!')
        : (L.spend_more || 'Spend {{ amount }} more for {{ reward }}!');

      return this._tpl(tpl, {
        amount: `<span class="spend-amount">${amount}</span>`,
        reward: `<span class="spend-emphasis">${name}</span>`
      });
    }

    _tpl(template, vars) {
      if (!template) return '';
      return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => vars[k] ?? '');
    }

    eligible(cart) {
      if (!cart || !Array.isArray(cart.items)) return parseInt(cart?.total_price, 10) || 0;
      return cart.items.reduce((s, item) => {
        const sku = (item.sku || '').toUpperCase();
        return (sku.includes('DESTASH') || sku.includes('ADVENT'))
          ? s : s + (parseInt(item.final_line_price, 10) || 0);
      }, 0);
    }

    money(cents, fmt) {
      if (typeof window.formatMoney === 'function') {
        try {
          const el = document.createElement('span');
          el.innerHTML = window.formatMoney(cents, fmt);
          return el.textContent;
        } catch {}
      }
      if (typeof cents === 'string') cents = parseFloat(cents);
      if (isNaN(cents)) cents = 0;
      const v = (cents / 100).toFixed(2), n = Math.floor(cents / 100);
      return fmt
        .replace('{{amount_with_comma_separator}}', v.replace('.', ','))
        .replace('{{amount_no_decimals_with_comma_separator}}', n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','))
        .replace('{{amount_no_decimals}}', n.toString())
        .replace('{{amount_with_apostrophe_separator}}', v.replace('.', "'"))
        .replace('{{amount}}', v);
    }
  }

  customElements.define('spend-and-earn', SpendAndEarn);
}
